import { inject, Injectable } from '@angular/core';
import { Environment } from '@rma/dashboard/generic/util/environment';
import { FeatureFlags } from '@rma/dashboard/generic/util/feature-flags';
import { combineLatest, map, Observable } from 'rxjs';
import { COLLECT_GROUP_FEATURES, getCollectGroupItems } from '../entities/nav-items-collect';
import {
  getManageGroupItems,
  getSettingsGroupItems,
  MANAGE_GROUP_FEATURES,
  SettingGroupFeature,
  SETTINGS_GROUP_FEATURES,
} from '../entities/nav-items-manage';
import { getOverview, getProfile, NavigationUserType } from '../entities/nav-items-misc';
import { getMonitorGroupItems, MonitorGroupFeature, MONITORING_GROUP_FEATURES } from '../entities/nav-items-monitor';
import { filterShareItems, getShareGroupItems, SHARE_GROUP_FEATURES } from '../entities/nav-items-share';
import { createNavigationGroup, NavigationGooglePermissions, NavigationGroup } from '../entities/side-nav-items';
import { SideNavigationConfigService } from './side-navigation-config.service';

@Injectable({
  providedIn: 'root',
})
export class OfficeSideMenuService {
  private readonly config = inject(SideNavigationConfigService);
  private readonly environment = inject(Environment);

  public getNavigation(): Observable<NavigationGroup[]> {
    return combineLatest({
      enableDatafeeds: this.config.getLiveFeature(FeatureFlags.EnableDataFeeds, false),
      enableOfficeAwards: this.config.getLiveFeature(FeatureFlags.EnableOfficeAwards, false),
      googlePermissions: this.config.getGoogleConfig$,
      hasLeasing: this.config.getLiveFeature(FeatureFlags.EnableLeasing, false),
      hasReviews: this.config.getSubscriptionAccess('HasReviewManagement'),
      marketReport: this.config.getMarketReportConfig$,
      showIntegrations: this.config.getLiveFeature(FeatureFlags.DashboardEnableIntegrations, false),
      showLeaderboards: this.config.getLiveFeature(FeatureFlags.DashboardEnableAgencyLeaderboard, false),
      showCsMenuItem: this.config.showSocialStudioLink$,
      showSocialMediaManager: this.config.showSocialMediaManager$,
      showGoogle: this.config.showGoogle$,
      showPromoter: this.config.showPromoter$,
      showNps: this.config.showNps$,
      showReviewLinks: this.config.showReviewLinks$,
      showBillingAndSubs: this.config.showBillingAndSubs$,
    }).pipe(
      map(
        ({
          enableDatafeeds,
          enableOfficeAwards,
          googlePermissions,
          hasLeasing,
          hasReviews,
          marketReport,
          showIntegrations,
          showLeaderboards,
          showCsMenuItem,
          showSocialMediaManager,
          showGoogle,
          showPromoter,
          showNps,
          showReviewLinks,
          showBillingAndSubs,
        }) => {
          const { baseUrl } = this.environment;
          const { hasMarketReportAccess, hideMarketReport } = marketReport;
          const userType: NavigationUserType = 'agency';
          return [
            createNavigationGroup([getProfile()]),
            createNavigationGroup([getOverview()]),
            this.getCollectGroup(userType, hasReviews, hasLeasing, showNps),
            this.getMonitorGroup(showLeaderboards),
            this.getManageItems(baseUrl, enableDatafeeds, showReviewLinks, showBillingAndSubs),
            this.getShareGroup(
              userType,
              showIntegrations,
              hideMarketReport,
              hasMarketReportAccess,
              enableOfficeAwards,
              showCsMenuItem,
              showSocialMediaManager,
              googlePermissions,
              baseUrl,
              showPromoter,
              showGoogle,
            ),
          ].filter((group) => group.items.length);
        },
      ),
    );
  }

  private getCollectGroup(userType: NavigationUserType, hasReviews: boolean, hasLeasing: boolean, showNps: boolean) {
    const notAvailable = hasLeasing ? ['pending'] : ['pending', 'leasing'];
    const features = COLLECT_GROUP_FEATURES.filter((f) => !notAvailable.includes(f) || !hasReviews).filter((f) => showNps || f !== 'nps');
    const items = getCollectGroupItems(features, userType);
    return createNavigationGroup(items, 'collect');
  }

  private getShareGroup(
    userType: NavigationUserType,
    showIntergations: boolean,
    hideMarketReport: boolean,
    hasMarketReportAccess: boolean,
    hasAwards: boolean,
    showCsMenuItem: boolean,
    showSocialMediaManager: boolean,
    googlePermissions: NavigationGooglePermissions,
    baseUrl: string,
    showPromoter: boolean,
    showGoogle: boolean,
  ): NavigationGroup {
    const features = SHARE_GROUP_FEATURES.filter(
      filterShareItems(showIntergations, hideMarketReport, hasAwards, showCsMenuItem, showSocialMediaManager, showPromoter, showGoogle),
    );

    const items = getShareGroupItems(features, userType, hasMarketReportAccess, googlePermissions, baseUrl);

    return createNavigationGroup(items, 'share');
  }

  private getMonitorGroup(showLeaderboards: boolean): NavigationGroup {
    const notAvailable: MonitorGroupFeature[] = !showLeaderboards ? ['leaderboards'] : [];
    const features = MONITORING_GROUP_FEATURES.filter((f) => !notAvailable.includes(f));

    return createNavigationGroup(getMonitorGroupItems(features), 'monitor');
  }

  private getManageItems(
    baseUrl: string,
    enableDatafeeds: boolean,
    showReviewLinks: boolean,
    showBillingAndSubs: boolean,
  ): NavigationGroup {
    const features = MANAGE_GROUP_FEATURES.filter((i) => showBillingAndSubs || i !== 'billingAndSubs');

    return createNavigationGroup(
      [...getManageGroupItems(features), getSettingsGroupItems(this.getSettingsItems(enableDatafeeds, showReviewLinks), baseUrl)],
      'manage',
    );
  }

  private getSettingsItems(enableDatafeeds: boolean, showReviewLinks: boolean): SettingGroupFeature[] {
    const notAvailable: SettingGroupFeature[] = ['assistants'];

    if (!enableDatafeeds) {
      notAvailable.push('dataFeeds');
    }

    if (!showReviewLinks) {
      notAvailable.push('reviewLinks');
    }

    const items = SETTINGS_GROUP_FEATURES.filter((i) => !notAvailable.includes(i));
    return items;
  }
}
